import { useEffect, useState } from 'react';

import {
  alpha,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Close, Delete } from '@material-ui/icons';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import { Autocomplete } from '@material-ui/lab';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import * as yup from 'yup';

import DrawerBackHeader from 'components/DrawerBackHeader';
import { DOCUMENT_TYPES_OPTIONS, DocumentType } from 'constants/document';
import { useAppDispatch, useAppSelector } from 'store';
import {
  ContactFunnelOpportunity,
  fetchFunnelsAndOpportunities,
} from 'store/reducers/contatos';
import {
  fetchFunnelsSelect,
  fetchFunnelTags,
  fetchOpportunityDetails,
  fetchOpportunitySources,
  funnelsSelector,
  FunnelTag,
  OpportunitySource,
  updateFunnelStageOpportunity,
  UpdateFunnelStageOpportunityPayload,
} from 'store/reducers/funnels';
import {
  currencyBrlMask,
  formatCurrency,
  parseCurrencyBRLToNumber,
} from 'utils/currencyUtils';
import { documentMask } from 'utils/documentMask';
import emptyStringToNull from 'utils/emptyStringToNull';

import DeleteOpportunityConfirm from './DeleteOpportunityConfirm';
import MoveOpportunityFunnelForm from './MoveOpportunityFunnelForm';
import OpportunityHistoryDrawer from './OpportunityHistoryDrawer';
import OpportunityTags from './OpportunityTags';

const useStyles = makeStyles(({ appDrawer, palette, spacing }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
  },
  divider: {
    margin: spacing(1, -3),
  },
  sectionTitle: {
    fontWeight: 500,
    color: alpha(palette.primary.main, 0.7),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  funnelSection: {
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    paddingTop: spacing(2),
    marginLeft: spacing(-3),
    marginRight: spacing(-3),
    backgroundColor: palette.background.default,
  },
  funnelDivider: {
    marginLeft: spacing(-3),
    marginRight: spacing(-3),
    marginTop: spacing(3),
  },
  listItem: {
    marginTop: 0,
    marginBottom: 0,
  },
  tagsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
  documentTypeAdornment: {
    marginRight: spacing(2),
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
  },
}));

const opportunityValidationSchema = yup.object({
  description: yup.string().optional(),
  value: yup.string().optional(),
  source: yup.object().nullable(),
  businessName: yup.string().optional(),
  documentType: yup.mixed<DocumentType>(),
  documentNumber: yup.string().when('documentType', {
    is: (value: DocumentType) => !!value,
    then: schema => schema.required('O número do documento é obrigatório'),
    otherwise: schema => schema.notRequired(),
  }),
});

type OpportunityFormValues = {
  description: string;
  value: string;
  source: OpportunitySource | null;
  tags: FunnelTag[];
  businessName: string;
  documentType: string;
  documentNumber: string;
};

type OpportunityDetailsDrawerProps = {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  opportunity: ContactFunnelOpportunity;
  onSuccessDeleteOpportunity?: () => void;
};

function OpportunityDetailsDrawer(props: OpportunityDetailsDrawerProps) {
  const { open, onClose, funnelId, opportunity, onSuccessDeleteOpportunity } =
    props;
  const theme = useTheme();
  const confirm = useConfirm();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [isMoveFormPending, setIsMoveFormPending] = useState(false);

  const { stageId, id: opportunityId } = opportunity;

  const { opportunitySources, opportunityDetails, loadingOpportunitySources } =
    useAppSelector(funnelsSelector);

  const handleRefreshOpportunityData = () => {
    const contactId = opportunityDetails?.contact.id;

    dispatch(fetchOpportunityDetails({ funnelId, opportunityId }));

    if (contactId) {
      dispatch(fetchFunnelsAndOpportunities(contactId));
    }
  };

  const formik = useFormik<OpportunityFormValues>({
    enableReinitialize: true,
    initialValues: {
      description: opportunityDetails?.description ?? '',
      value: opportunityDetails?.opportunityValue
        ? formatCurrency(opportunityDetails.opportunityValue)
        : '',
      source: opportunityDetails?.source ?? null,
      tags: (opportunityDetails?.tags as FunnelTag[]) ?? [],
      businessName: opportunityDetails?.businessName ?? '',
      documentType: opportunityDetails?.documentType ?? '',
      documentNumber: opportunityDetails?.documentNumber ?? '',
    },
    validationSchema: opportunityValidationSchema,
    onSubmit: async (values: OpportunityFormValues) => {
      const payload: UpdateFunnelStageOpportunityPayload = {
        funnelId,
        stageId,
        opportunityId,
        value: values.value ? parseCurrencyBRLToNumber(values.value) : null,
        description: emptyStringToNull(values.description),
        documentType: emptyStringToNull(values.documentType),
        documentNumber: emptyStringToNull(values.documentNumber),
        businessName: emptyStringToNull(values.businessName),
        sourceId: emptyStringToNull(values.source?.id),
      };

      await dispatch(updateFunnelStageOpportunity(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Oportunidade atualizada com sucesso');
          setIsEditing(false);
          handleRefreshOpportunityData();
        })
        .catch(error => {
          if (error.response) {
            enqueueSnackbar(error.response.data.message);
          }
        });
    },
  });

  const { dirty } = formik;

  const descriptionError =
    formik.touched.description && formik.errors.description;
  const valueError = formik.touched.value && formik.errors.value;
  const documentNumberError =
    formik.touched.documentNumber && formik.errors.documentNumber;

  const handleToggleEditMode = () => {
    setIsEditing(prev => !prev);
  };

  const handleCloseDrawer = () => {
    onClose();

    if (onSuccessDeleteOpportunity) {
      onSuccessDeleteOpportunity();
    }
  };

  const handleClearDocumentType = () => {
    formik.setFieldValue('documentType', '');
    formik.setFieldValue('documentNumber', '');
  };

  const [showOpportunityHistoryDrawer, hideOpportunityHistoryDrawer] = useModal(
    ({ in: open }) => {
      return (
        <OpportunityHistoryDrawer
          funnelId={funnelId}
          opportunityId={opportunityId}
          open={open}
          onClose={hideOpportunityHistoryDrawer}
          contactName={
            opportunityDetails?.contact?.name ||
            opportunityDetails?.contact?.contact
          }
          opportunityCode={opportunityDetails?.code}
        />
      );
    },
    [funnelId, opportunityId, opportunityDetails],
  );

  const [showDeleteOpportunityConfirm, hideDeleteOpportunityConfirm] = useModal(
    ({ in: open }) => {
      return (
        <DeleteOpportunityConfirm
          funnelId={funnelId}
          opportunityIds={[opportunityId]}
          open={open}
          onClose={hideDeleteOpportunityConfirm}
          onSuccess={handleCloseDrawer}
        />
      );
    },
    [funnelId, opportunityId],
  );

  const handleClose = () => {
    if ((isEditing && dirty) || isMoveFormPending) {
      confirm({
        title: <Typography variant="body2">Descartar alterações?</Typography>,
        description: (
          <Typography variant="caption">
            Vocês realizou alterações que ainda não foram salvas. <br /> Deseja
            descartar mesmo assim?
          </Typography>
        ),
        cancellationText: 'Continuar editando',
        confirmationText: 'Sim, descartar',
        confirmationButtonProps: { variant: 'contained', color: 'secondary' },
        dialogProps: { maxWidth: 'xs' },
      })
        .then(() => {
          onClose();
        })
        .catch(() => {
          //
        });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (open && opportunity && funnelId) {
      dispatch(fetchOpportunityDetails({ funnelId, opportunityId }));
      dispatch(fetchFunnelTags({ funnelId }));
    }
  }, [open, opportunity, funnelId, dispatch, opportunityId]);

  useEffect(() => {
    if (open) {
      dispatch(fetchOpportunitySources());
      dispatch(fetchFunnelsSelect());
    }
  }, [open, dispatch]);

  return (
    <Drawer
      transitionDuration={0}
      open={open}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      ModalProps={{
        BackdropProps: {
          style: {
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <DrawerBackHeader title="Detalhes da oportunidade" onBack={handleClose} />

      <FormikProvider value={formik}>
        <DialogContent className={classes.content}>
          {/* HEADER */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <ListItemText
                primaryTypographyProps={{
                  variant: 'button',
                }}
                primary={
                  opportunityDetails?.contact?.name ||
                  opportunityDetails?.contact?.contact
                }
                secondary={`Código: ${opportunityDetails?.code}`}
              />
            </Grid>

            <Grid
              item
              container
              alignItems="center"
              spacing={1}
              style={{ width: 'auto' }}
            >
              <Grid item style={{ display: 'flex' }}>
                <AccessTimeIcon color="action" fontSize="small" />
              </Grid>
              <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption">
                  {dayjs(opportunityDetails?.createdAt).fromNow()}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* FUNIL SECTION */}
          <Box className={classes.funnelSection}>
            <Box mb={1} className={classes.sectionHeader}>
              <Typography variant="body2" className={classes.sectionTitle}>
                Funil
              </Typography>
              <Button
                onClick={showOpportunityHistoryDrawer}
                startIcon={<HistoryIcon />}
                size="small"
                color="inherit"
              >
                Ver histórico
              </Button>
            </Box>

            <MoveOpportunityFunnelForm
              onCloseDrawer={handleCloseDrawer}
              onPendingChange={setIsMoveFormPending}
              currentStageStatus={opportunityDetails?.stageStatus}
              funnelId={funnelId}
              stage={opportunityDetails?.stage}
              opportunityId={opportunityId}
              onSuccessMove={handleRefreshOpportunityData}
            />

            <Divider className={classes.funnelDivider} />
          </Box>

          {/* DETALHES DA NEGOCIAÇÃO */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  Detalhes da negociação
                </Typography>

                {!isEditing && (
                  <IconButton size="small" onClick={handleToggleEditMode}>
                    <EditIcon fontSize="small" color="action" />
                  </IconButton>
                )}
              </Box>
            </Grid>

            {isEditing ? (
              <Grid item container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="description"
                    label="Descrição"
                    placeholder="Descreva os detalhes da negociação"
                    multiline
                    minRows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={Boolean(descriptionError)}
                    helperText={descriptionError}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="value"
                    label="Valor"
                    placeholder="R$ 0,00"
                    value={formik.values.value}
                    onChange={event => {
                      const masked = currencyBrlMask(event.target.value);
                      formik.setFieldValue('value', masked);
                    }}
                    error={Boolean(valueError)}
                    helperText={valueError}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid item container spacing={2}>
                <Grid item xs={12}>
                  <ListItemText
                    className={classes.listItem}
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary="Descrição"
                    secondary={opportunityDetails?.description || '-'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ListItemText
                    className={classes.listItem}
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary="Valor"
                    secondary={formatCurrency(
                      opportunityDetails?.opportunityValue || 0,
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>

          <Divider className={classes.divider} />

          {/* INFORMAÇÕES DA OPORTUNIDADE */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box className={classes.sectionHeader}>
                <Typography variant="body2" className={classes.sectionTitle}>
                  Informações da oportunidade
                </Typography>

                {!isEditing && (
                  <IconButton size="small" onClick={handleToggleEditMode}>
                    <EditIcon fontSize="small" color="action" />
                  </IconButton>
                )}
              </Box>
            </Grid>

            {/* TAGS */}
            <Grid item xs={12}>
              <Box className={classes.tagsContainer}>
                <Typography style={{ minWidth: 160 }} variant="body2">
                  Tags da oportunidade:
                </Typography>

                <OpportunityTags
                  inline
                  readOnly={!isEditing}
                  disabled={!isEditing}
                  funnelId={funnelId}
                  stageId={stageId}
                  opportunityId={opportunityId}
                  tags={opportunityDetails?.tags || []}
                  onSuccess={handleRefreshOpportunityData}
                />
              </Box>
            </Grid>

            {!isEditing && (
              <Grid item xs={12}>
                <ListItemText
                  className={classes.listItem}
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary="Criado por"
                  secondary={opportunityDetails?.createdBy?.name || '-'}
                />
              </Grid>
            )}

            {/* ORIGEM DO LEAD */}
            {isEditing ? (
              <Grid item xs={12}>
                <Autocomplete
                  id="sourceId"
                  options={opportunitySources || []}
                  loading={loadingOpportunitySources}
                  getOptionLabel={option => option.name}
                  getOptionSelected={(option, value) => option.id === value.id}
                  value={formik.values.source}
                  onChange={(event, newValue, reason) => {
                    if (newValue) formik.setFieldValue('source', newValue);
                    if (reason === 'clear')
                      formik.setFieldValue('source', null);
                  }}
                  noOptionsText={
                    <Typography variant="body2">
                      Nenhuma origem encontrada
                    </Typography>
                  }
                  clearText="Limpar"
                  closeText="Fechar"
                  openText="Abrir"
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Origem do lead"
                      placeholder="Pesquise"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingOpportunitySources && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <ListItemText
                  className={classes.listItem}
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary="Origem do lead"
                  secondary={opportunityDetails?.source?.name || '-'}
                />
              </Grid>
            )}

            {!isEditing && (
              <Grid item xs={12}>
                <ListItemText
                  className={classes.listItem}
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary="Data de criação"
                  secondary={dayjs(opportunityDetails?.createdAt).format(
                    'DD/MM/YY[, às] HH:mm',
                  )}
                />
              </Grid>
            )}

            {/* NEGÓCIO */}
            {isEditing ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="businessName"
                  label="Negócio"
                  variant="outlined"
                  placeholder="Digite"
                  value={formik.values.businessName}
                  onChange={formik.handleChange}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <ListItemText
                  className={classes.listItem}
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary="Negócio"
                  secondary={opportunityDetails?.businessName || '-'}
                />
              </Grid>
            )}

            {/* DOCUMENTO */}
            {isEditing ? (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      select
                      variant="outlined"
                      name="documentType"
                      placeholder="Tipo"
                      label="Documento"
                      value={formik.values.documentType || ''}
                      onChange={event => {
                        formik.handleChange(event);
                        formik.setFieldValue('documentNumber', '');
                      }}
                      InputProps={{
                        endAdornment: formik.values.documentType && (
                          <InputAdornment
                            className={classes.documentTypeAdornment}
                            position="end"
                          >
                            <IconButton
                              size="small"
                              onClick={handleClearDocumentType}
                              onMouseDown={e => e.preventDefault()}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    >
                      {DOCUMENT_TYPES_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="documentNumber"
                      label="Número do documento"
                      placeholder="Digite o número"
                      value={formik.values.documentNumber}
                      onChange={event => {
                        const masked = documentMask(
                          event.target.value,
                          formik.values.documentType as DocumentType,
                        );
                        formik.setFieldValue('documentNumber', masked);
                      }}
                      error={Boolean(documentNumberError)}
                      helperText={documentNumberError}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <ListItemText
                  className={classes.listItem}
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary="Documento"
                  secondary={opportunityDetails?.documentNumber || '-'}
                />
              </Grid>
            )}

            {!isEditing && (
              <>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>

                <Grid style={{ padding: 0, marginBottom: 24 }} item xs={12}>
                  <Button
                    startIcon={<Delete />}
                    onClick={showDeleteOpportunityConfirm}
                    fullWidth
                    style={{ color: theme.palette.error.main }}
                  >
                    Remover oportunidade
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        {isEditing && (
          <DialogActions className={classes.actions}>
            <Button onClick={handleToggleEditMode}>Cancelar</Button>
            <Button
              disabled={formik.isSubmitting}
              variant="contained"
              color="primary"
              onClick={formik.submitForm}
            >
              Salvar alterações
            </Button>
          </DialogActions>
        )}
      </FormikProvider>
    </Drawer>
  );
}

export default OpportunityDetailsDrawer;
